// Limpieza de datos personales ANTES de enviar nada a Sentry. Funciones puras (sin SDK), con
// prueba en scripts/test-sentry-scrub.ts. El sitio tiene datos de menores, salud y pagos: ante
// la duda se borra. Nada de esto se importa del SDK, así que no pesa ni carga Sentry.

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Teléfonos: +56 9 1234 5678, 912345678, (+56) 9-1234-5678, etc. (con +, 8 a 15 dígitos seguidos, o 9 xxxx xxxx); las fechas con guiones no calzan.
const PHONE = /\+\(?\d[\d\s().-]{6,}\d|(?<![\d-])\d{8,15}(?![\d-])|(?<!\d)9[\s.-]\d{4}[\s.-]\d{4}(?!\d)/g;
// Rutas con token: /padres/:token, /consentimiento/:token, /hoja-de-ruta/:token.
const RUTA_CON_TOKEN = /(\/(?:padres|consentimiento|hoja-de-ruta)\/)[^/?#\s"'<>)]+/gi;
// Parámetros de query sensibles (cualquier valor de query se borra salvo lista blanca vacía).
const QUERY_VALOR = /([?&][^=&#\s"'<>]+=)[^&#\s"'<>]*/g;

export const REDACTADO = "[borrado]";

/** Limpia un texto cualquiera (mensaje, URL, pila). */
export function scrubText(texto: string): string {
  return texto
    .replace(RUTA_CON_TOKEN, `$1${REDACTADO}`)
    .replace(QUERY_VALOR, `$1${REDACTADO}`)
    .replace(EMAIL, "[correo]")
    .replace(PHONE, "[telefono]");
}

/** Limpia una URL: token de ruta, TODOS los valores de query y el fragmento. */
export function scrubUrl(url: string): string {
  return scrubText(url.split("#")[0]);
}

function scrubValor(v: unknown, profundidad = 0): unknown {
  if (typeof v === "string") return scrubText(v);
  if (profundidad > 4 || v === null || typeof v !== "object") return v;
  if (Array.isArray(v)) return v.map((x) => scrubValor(x, profundidad + 1));
  const out: Record<string, unknown> = Object.create(null);
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = scrubValor(x, profundidad + 1);
  return out;
}

type Rol = "admin" | "profesor" | "alumno" | "visitante";
type Evento = {
  message?: string;
  user?: unknown;
  request?: { url?: string; headers?: unknown; cookies?: unknown; data?: unknown; query_string?: unknown };
  exception?: { values?: Array<{ value?: string; stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> } }> };
  breadcrumbs?: Array<{ category?: string; message?: string; data?: unknown }>;
  extra?: unknown;
  contexts?: Record<string, unknown>;
  tags?: Record<string, unknown>;
  [k: string]: unknown;
};

/** beforeSend: devuelve el evento limpio. Nunca conserva cuerpo de request, cookies, cabeceras
 *  ni usuario (a lo más el rol). */
export function scrubEvent<T extends Evento>(event: T, rol?: Rol): T {
  const e = event as Evento;
  if (e.message) e.message = scrubText(e.message);
  e.exception?.values?.forEach((val) => {
    if (val.value) val.value = scrubText(val.value);
    val.stacktrace?.frames?.forEach((f) => {
      if (f.filename) f.filename = scrubUrl(f.filename);
      if (f.abs_path) f.abs_path = scrubUrl(f.abs_path);
    });
  });
  if (e.request) {
    const url = e.request.url ? scrubUrl(e.request.url) : undefined;
    e.request = url ? { url } : {};
  }
  e.user = rol ? { segment: rol } : undefined;
  if (!e.user) delete e.user;
  if (e.contexts) delete e.contexts.user;
  if (e.breadcrumbs) e.breadcrumbs = e.breadcrumbs.map((b) => scrubBreadcrumb(b)).filter((b): b is NonNullable<typeof b> => !!b);
  if (e.extra) e.extra = scrubValor(e.extra);
  if (e.tags) e.tags = scrubValor(e.tags) as Record<string, unknown>;
  return event;
}

/** beforeBreadcrumb: descarta consola/fetch/xhr/clics (pueden llevar tokens o texto de formularios)
 *  y limpia navegación. */
export function scrubBreadcrumb<T extends { category?: string; message?: string; data?: unknown }>(b: T): T | null {
  const cat = b.category ?? "";
  if (cat === "console" || cat === "fetch" || cat === "xhr" || cat.startsWith("ui.")) return null;
  if (b.message) b.message = scrubText(b.message);
  if (b.data) b.data = scrubValor(b.data);
  return b;
}

/** Errores que no son del sitio: extensiones, ResizeObserver, red de terceros. */
export const IGNORAR_ERRORES: Array<string | RegExp> = [
  /ResizeObserver loop/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /Non-Error promise rejection captured/i,
  /^Script error\.?$/,
  /chrome-extension:|moz-extension:|safari-extension:|safari-web-extension:/,
  /Failed to fetch|NetworkError when attempting to fetch|Load failed/i,
];
export const IGNORAR_URLS: Array<string | RegExp> = [
  /^chrome-extension:/, /^moz-extension:/, /^safari-(web-)?extension:/,
  /googletagmanager\.com/, /google-analytics\.com/, /gstatic\.com\/recaptcha/, /google\.com\/recaptcha/,
];
